import React, { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import PropTypes from 'prop-types';
import { withLocalize } from 'react-localize-redux';
import { Spinner, Shelf, WarningMessage, Button } from '../../components/widgets';
import EditOrg from './EditOrg';
import EditOrgAdAccounts from './EditOrgAdAccounts';
import EditSegment from './EditSegment';
import css from './EditShelf.css';
import { GET_AB_TESTING_SEGMENT } from '../../audiencesABTesting/queries';

const EditShelf = props => {
    const {
        edit = null,
        editError = null,
        editCancel,
        editConfirm,
        saving = false,
        editChange,
        toggleAdAccount,
        segments,
        editSegmentStart,
        editSegmentChange,
        editSegmentSave,
        editSegmentCancel,
        adminMode = false,
        manualRun = () => {},
    } = props;

    const { data, loading } = useQuery(GET_AB_TESTING_SEGMENT, { skip: !edit });

    const segmentsWithAbTest = useMemo(() => {
        if (loading || edit == null) return [];
        return (
            data?.abTestingMetadata
                .filter(test => test.testChannels?.some(channel => channel.adnetworkIntegrationId === edit.id))
                .map(test => test.segment.id) || []
        );
    }, [edit?.id, data, loading]);

    const innerOrg =
        edit && Object.keys(edit).indexOf('ad_accounts') > 0 ? (
            <EditOrgAdAccounts toggleAdAccount={toggleAdAccount} edit={edit} />
        ) : (
            <EditOrg
                editChange={editChange}
                edit={edit}
                fields={edit && edit.distribution_fields}
                editConfirm={editConfirm}
            />
        );

    return (
        <Shelf
            open={!!edit}
            headerText={`${edit ? edit.display_name : ''} Configuration`}
            onClose={editCancel}
            shelfSize="medium"
        >
            {!edit ? (
                <Spinner show className={css.spinner} />
            ) : (
                <React.Fragment>
                    <WarningMessage
                        show={!!editError && edit && !saving}
                        message={editError || ''}
                        showIcon={false}
                        type="error"
                    />
                    <div className={css.editContainer}>
                        <div className={css.editTop}>
                            {adminMode && edit.distribution.id ? (
                                <div style={{ textAlign: 'right' }}>
                                    <Button onClick={manualRun} type="secondary">
                                        Manual Run (Admin Only)
                                    </Button>
                                </div>
                            ) : null}
                            <h4>Top Level Configuration</h4>
                            {innerOrg}
                        </div>
                        {edit.distribution.id && edit.distribution.id > 0 && edit.distribution.active && (
                            <React.Fragment>
                                <h4>Segment Configuration</h4>
                                <div className={css.editContent}>
                                    {segments.map(seg => (
                                        <EditSegment
                                            key={seg.id}
                                            segment={seg}
                                            isAbTestConnected={segmentsWithAbTest.includes(seg.id)}
                                            editSegmentStart={editSegmentStart}
                                            editSegmentChange={editSegmentChange}
                                            editSegmentSave={editSegmentSave}
                                            editSegmentCancel={editSegmentCancel}
                                        />
                                    ))}
                                </div>
                            </React.Fragment>
                        )}
                    </div>
                </React.Fragment>
            )}
        </Shelf>
    );
};

EditShelf.propTypes = {
    edit: PropTypes.shape({
        display_name: PropTypes.string,
        ad_accounts: PropTypes.array,
        distribution_fields: PropTypes.array,
        distribution: PropTypes.shape({
            id: PropTypes.number,
            active: PropTypes.bool,
        }),
    }),
    editError: PropTypes.string,
    editCancel: PropTypes.func.isRequired,
    editConfirm: PropTypes.func.isRequired,
    saving: PropTypes.bool,
    editChange: PropTypes.func.isRequired,
    toggleAdAccount: PropTypes.func.isRequired,
    segments: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.number.isRequired,
        })
    ).isRequired,
    editSegmentStart: PropTypes.func.isRequired,
    editSegmentChange: PropTypes.func.isRequired,
    editSegmentSave: PropTypes.func.isRequired,
    editSegmentCancel: PropTypes.func.isRequired,
    adminMode: PropTypes.bool,
    manualRun: PropTypes.func,
};

export default withLocalize(EditShelf, 'locale');
